// Collects the header keys actually in use by each object_input attribute, so a
// mapping onto a headers field can offer real keys instead of free text. Keyed
// by `${integrationId}.${attributeName}`.

import { useEffect, useRef, useState } from 'react';

import ConfiguredIntegrationsApi from '../../../api/configured_integrations';
import { locationAttributesOf } from './sharedSettingModel';

const LIST_PAGE_SIZE = 25;
// The list endpoint often returns attribute values already; only when none of
// them carry usable headers is it worth fetching individual records, and then
// only a handful.
const DETAIL_FETCH_LIMIT = 5;

const attributeValueOf = (configuredIntegration, name) =>
  (configuredIntegration.attributes?.location_settings_attributes || []).find(
    (a) => a.name === name,
  );

const hasUsableValues = (configuredIntegrations, attributes) =>
  attributes.some((attr) =>
    configuredIntegrations.some((ci) => {
      const value = attributeValueOf(ci, attr.name)?.value;
      return Array.isArray(value) && value.length > 0;
    }),
  );

const keysFor = (configuredIntegrations, name) => {
  const keys = new Set();

  configuredIntegrations.forEach((ci) => {
    const value = attributeValueOf(ci, name)?.value;
    if (!Array.isArray(value)) return;
    value.forEach(([key]) => {
      if (key) keys.add(key);
    });
  });

  return [...keys];
};

const loadForIntegration = async (integration) => {
  const updates = {};
  const objectInputAttrs = locationAttributesOf(integration).filter(
    (a) => a.html_tag === 'object_input',
  );
  if (objectInputAttrs.length === 0) return updates;

  const { data: ciList } =
    await ConfiguredIntegrationsApi.fetchConfiguredIntegrations(
      integration.id,
      1,
      LIST_PAGE_SIZE,
    );
  if (!ciList?.length) return updates;

  let configuredIntegrations = ciList;
  if (!hasUsableValues(ciList, objectInputAttrs)) {
    const fetched = await Promise.all(
      ciList.slice(0, DETAIL_FETCH_LIMIT).map(async (ci) => {
        const { data } =
          await ConfiguredIntegrationsApi.fetchConfiguredIntegrationsByUuid(
            ci.id,
          );
        return Array.isArray(data) ? data[0] : data;
      }),
    );
    configuredIntegrations = fetched.filter(Boolean);
  }

  objectInputAttrs.forEach((attr) => {
    const keys = keysFor(configuredIntegrations, attr.name);
    if (keys.length > 0) {
      updates[`${integration.id}.${attr.name}`] = keys;
    }
  });

  return updates;
};

export default function useHeaderKeys(selectedIntegrations) {
  const [headerKeysByAttr, setHeaderKeysByAttr] = useState({});
  // Integrations whose keys have already been requested. Without this, adding
  // the nth integration re-fetches the n-1 already loaded — each one costing a
  // list call plus up to five detail calls, so the cost grows quadratically
  // across a session. Keys are never dropped from state, so an integration that
  // is removed and re-added still has its keys.
  const requestedRef = useRef(new Set());

  useEffect(() => {
    const pending = selectedIntegrations.filter(
      (integration) => !requestedRef.current.has(integration.id),
    );
    if (pending.length === 0) return;

    // Marked before awaiting, so a re-render mid-flight does not fire again.
    pending.forEach((integration) => requestedRef.current.add(integration.id));

    async function loadHeaderKeys() {
      // allSettled, not all: one integration failing must not discard the keys
      // the others returned, and a failure has to release its mark so the next
      // selection change can try again — otherwise a single transient error
      // leaves that integration without key pickers for the whole session.
      const results = await Promise.allSettled(
        pending.map((integration) => loadForIntegration(integration)),
      );

      const updates = {};
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          Object.assign(updates, result.value);
          return;
        }
        requestedRef.current.delete(pending[index].id);
      });

      if (Object.keys(updates).length > 0) {
        setHeaderKeysByAttr((prev) => ({ ...prev, ...updates }));
      }
    }

    loadHeaderKeys();
  }, [selectedIntegrations]);

  return headerKeysByAttr;
}
